import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  formatInquiryDisplayDate,
  getInquiryFieldsFromBatchItem,
  toInquiryInputDate,
} from "./inquiryApplicationUtils";

export const INQUIRY_BATCH_EXPORT_HEADERS = [
  "Student Name",
  "Student ID",
  "Student DOB",
  "Course",
  "Commence Date",
  "Finish Date",
  "Intake",
];

export function sortBatchItemsByProvider(items = []) {
  return [...items].sort((left, right) => {
    const providerCompare = String(left?.provider_name || "")
      .trim()
      .localeCompare(String(right?.provider_name || "").trim(), undefined, {
        sensitivity: "base",
      });
    if (providerCompare !== 0) return providerCompare;

    return String(left?.student_name || "")
      .trim()
      .localeCompare(String(right?.student_name || "").trim(), undefined, {
        sensitivity: "base",
      });
  });
}

export function filterBatchItemsByProvider(items = [], searchTerm = "") {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return items;
  return items.filter((item) =>
    String(item?.provider_name || "")
      .toLowerCase()
      .includes(query),
  );
}

export function buildInquiryBatchExportRow(item = {}, batch = {}) {
  const fields = getInquiryFieldsFromBatchItem(item, batch);
  return {
    "Student Name": String(fields.clientName || item.student_name || "").trim(),
    "Student ID": String(fields.studentId || item.student_id || "").trim(),
    "Student DOB":
      formatInquiryDisplayDate(fields.dob || item.dob) ||
      toInquiryInputDate(fields.dob || item.dob) ||
      "",
    Course: String(fields.course || item.course_name || "").trim(),
    "Commence Date":
      formatInquiryDisplayDate(fields.start || item.start_date) ||
      toInquiryInputDate(fields.start || item.start_date) ||
      "",
    "Finish Date":
      formatInquiryDisplayDate(fields.end || item.end_date) ||
      toInquiryInputDate(fields.end || item.end_date) ||
      "",
    Intake: String(fields.intake || item.intake || "").trim(),
  };
}

export function resolveInquiryBatchExportItems(items = [], selectedIds = null) {
  const sorted = sortBatchItemsByProvider(items);
  const selectedSet =
    selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);

  if (selectedSet.size > 0) {
    return sorted.filter((item) => selectedSet.has(Number(item.id)));
  }

  return sorted;
}

export function buildInquiryBatchExportRows(items = [], batch = {}) {
  return resolveInquiryBatchExportItems(items).map((item) =>
    buildInquiryBatchExportRow(item, batch),
  );
}

function buildExportFileBaseName(batch = {}) {
  const batchNo = String(batch?.batch_no || batch?.id || "batch").replace(
    /[^\w-]+/g,
    "_",
  );
  const date = new Date().toISOString().slice(0, 10);
  return `Inquiry_Batch_${batchNo}_${date}`;
}

export function downloadInquiryBatchExcel(items = [], batch = {}, selectedIds = null) {
  const exportItems = resolveInquiryBatchExportItems(items, selectedIds);
  const rows = exportItems.map((item) => buildInquiryBatchExportRow(item, batch));

  if (!rows.length) {
    throw new Error("No student records to export.");
  }

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: INQUIRY_BATCH_EXPORT_HEADERS,
  });
  worksheet["!cols"] = INQUIRY_BATCH_EXPORT_HEADERS.map((header) => ({
    wch: Math.max(
      header.length + 2,
      ...rows.map((row) => String(row[header] || "").length + 2),
    ),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${buildExportFileBaseName(batch)}.xlsx`);
}

export function downloadInquiryBatchPdf(items = [], batch = {}, selectedIds = null) {
  const exportItems = resolveInquiryBatchExportItems(items, selectedIds);
  const rows = exportItems.map((item) => buildInquiryBatchExportRow(item, batch));

  if (!rows.length) {
    throw new Error("No student records to export.");
  }

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const title = batch?.subject || batch?.batch_name || batch?.batch_no || "Inquiry batch";
  const subtitle = [
    batch?.batch_no ? `Batch ${batch.batch_no}` : null,
    exportItems.length === 1 ? "1 student" : `${exportItems.length} students`,
  ]
    .filter(Boolean)
    .join(" · ");

  doc.setFontSize(16);
  doc.setTextColor(40);
  doc.text(title, 40, 36);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(subtitle, 40, 52);

  autoTable(doc, {
    head: [INQUIRY_BATCH_EXPORT_HEADERS],
    body: rows.map((row) =>
      INQUIRY_BATCH_EXPORT_HEADERS.map((header) => row[header] || ""),
    ),
    startY: 64,
    margin: { left: 28, right: 28 },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [0, 123, 255],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  doc.save(`${buildExportFileBaseName(batch)}.pdf`);
}
